import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetHomepageSettingsQuery,
  useSubmitContactFormMutation,
} from "../redux/services/api";
import { setSettings } from "../redux/slices/homepageSlice";
import { selectSettings } from "../redux/slices/homepageSlice";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "./StatusMessage";
import AOS from "aos";
import "aos/dist/aos.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faFacebook,
  faTwitter,
  faLinkedin,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";

const ContactUs = () => {
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const { data: settingsData } = useGetHomepageSettingsQuery();
  const { t, isArabic } = useTranslation();

  // Use RTK Query mutation for contact form submission
  const [submitContactForm, { isLoading: isSubmitting }] =
    useSubmitContactFormMutation();

  useEffect(() => {
    if (settingsData && settingsData.success) {
      dispatch(setSettings(settingsData.settings));
    }
  }, [settingsData, dispatch]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const result = await submitContactForm(formData).unwrap();

      if (result.success) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      setSubmitStatus("error");
    }
  };

  // Get contact information from settings or use defaults
  const contactInfo = {
    phone: settings?.contact?.phone || "+971 4 123 4567",
    email: settings?.contact?.email || "info@silverfort.com",
    address: settings?.contact?.address || "Business Bay, Dubai, UAE",
    whatsapp: settings?.contact?.whatsapp || "",
    socialMedia: settings?.socialMedia || {
      facebook: "https://facebook.com/silverfort",
      twitter: "https://twitter.com/silverfort",
      linkedin: "https://linkedin.com/company/silverfort",
      instagram: "https://instagram.com/silverfort",
    },
  };

  return (
    <section className="contact-us" id="contact">
      <div className="container">
        <div data-aos="fade-up" data-aos-delay="100">
          <h2 className="section-title">{t("contact.title")}</h2>
          <p className="section-subtitle">{t("contact.subtitle")}</p>
        </div>

        <div className="contact-content">
          {/* Contact Information */}
          <div
            className="contact-info"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            <div className="contact-item">
              <div className="contact-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.phone")}</h3>
                <p>{contactInfo.phone}</p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.email")}</h3>
                <p>{contactInfo.email}</p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.address")}</h3>
                <p>
                  {isArabic && contactInfo.addressAr
                    ? contactInfo.addressAr
                    : contactInfo.address}
                </p>
              </div>
            </div>

            {contactInfo.whatsapp && (
              <div className="contact-item">
                <div className="contact-icon">
                  <FontAwesomeIcon icon={faWhatsapp} />
                </div>
                <div className="contact-details">
                  <h3>WhatsApp</h3>
                  <a
                    href={contactInfo.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    <p>{contactInfo.whatsapp}</p>
                  </a>
                </div>
              </div>
            )}

            <div className="contact-item">
              <div className="contact-icon">
                <FontAwesomeIcon icon={faFacebook} />
              </div>
              <div className="contact-details">
                <h3>{t("contact.followUs")}</h3>
                <div className="social-media">
                  <a
                    href={contactInfo.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <FontAwesomeIcon icon={faFacebook} />
                  </a>
                  <a
                    href={contactInfo.socialMedia.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                  >
                    <FontAwesomeIcon icon={faTwitter} />
                  </a>
                  <a
                    href={contactInfo.socialMedia.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <FontAwesomeIcon icon={faLinkedin} />
                  </a>
                  <a
                    href={contactInfo.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className="contact-form"
            data-aos="fade-right"
            data-aos-delay="300"
          >
            <h3 className="form-title">{t("contact.title")}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">{t("contact.name")}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={t("contact.placeholders.name")}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t("contact.email")}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder={t("contact.placeholders.email")}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">{t("contact.phone")}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t("contact.placeholders.phone")}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">{t("contact.subject")}</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder={t("contact.placeholders.subject")}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">{t("contact.message")}</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  placeholder={t("contact.placeholders.message")}
                ></textarea>
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("common.loading") : t("contact.send")}
              </button>

              <StatusMessage
                message={
                  submitStatus === "success"
                    ? "Thank you! Your message has been sent successfully."
                    : submitStatus === "error"
                    ? "Sorry, there was an error sending your message. Please try again."
                    : null
                }
                type={
                  submitStatus === "success"
                    ? "success"
                    : submitStatus === "error"
                    ? "error"
                    : "info"
                }
                show={!!submitStatus}
                autoClose={submitStatus === "success"}
                autoCloseDelay={5000}
              />
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
